import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'wouter';
import { ArrowRight, CalendarDays, Compass, MapPin, Users } from 'lucide-react';

import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { isUnauthorizedError, redirectToLogin } from '@/lib/authUtils';
import { usePageContent } from '@/hooks/usePageContent';
import { useGeographyContext } from '@/hooks/useGeographyContext';
import { PublicShell } from '@/components/public-shell';
import CommunitySection from '@/components/community-section';
import ClassCard from '@/components/class-card';
import CreatorCard from '@/components/creator-card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  formatEnrollmentStatusLabel,
  getEnrollmentDashboardGroups,
  type DashboardEnrollmentRecord,
} from '@/lib/enrollment-dashboard';
import { formatDateCompact } from '@/lib/dateUtils';
import { formatSessionTime } from '@/lib/geography';
import { getGeographyLinks } from '@/lib/public-navigation';

interface HomeEnrollmentData extends DashboardEnrollmentRecord {
  id: string;
  classId: string;
  classTitle?: string | null;
  creatorName?: string | null;
  sessionStartDateTime?: string | null;
  sessionEndDateTime?: string | null;
  orderTotal?: number;
  quantity?: number;
  status?: string;
}

interface FeaturedClassCardData {
  id: string;
  title: string;
  description: string;
  price: string | number;
  pricingMode?: string | null;
  suggestedPrice?: string | number | null;
  promoEnabled?: boolean | null;
  promoPrice?: string | number | null;
  thumbnailUrl?: string | null;
  rating?: string | number | null;
  creator?: {
    id: string;
    displayName: string;
    profileImageUrl?: string | null;
  } | null;
  enrollmentCount?: number;
  spotsLeft?: number | null;
}

interface FeaturedCreatorCardData {
  id: string;
  displayName: string;
  creatorHeadline: string;
  bio?: string | null;
  profileImageUrl?: string | null;
  isVerified?: boolean;
}

export default function Home() {
  const { toast } = useToast();
  const { isAuthenticated, isLoading, user } = useAuth();
  const { region, locality } = useGeographyContext();
  const { getContent } = usePageContent('home');

  const { data: featuredClasses = [], error: classesError } = useQuery<FeaturedClassCardData[]>({
    queryKey: ['/api/classes/featured'],
  });

  const { data: featuredCreators = [], error: creatorsError } = useQuery<FeaturedCreatorCardData[]>(
    {
      queryKey: ['/api/creators/featured'],
    }
  );

  const { data: enrollments = [], error: enrollmentsError } = useQuery<HomeEnrollmentData[]>({
    queryKey: ['/api/enrollments'],
  });

  const { data: publicStats = null, error: statsError } = useQuery<{
    activeCreators: number;
    availableClasses: number;
    totalSessions: number;
    totalStudents: number;
  }>({
    queryKey: ['/api/public-stats'],
  });

  useEffect(() => {
    const errors = [classesError, creatorsError, enrollmentsError, statsError].filter(Boolean);
    errors.forEach(error => {
      if (error && isUnauthorizedError(error as Error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        redirectToLogin(500);
      }
    });
  }, [classesError, creatorsError, enrollmentsError, statsError, toast]);

  if (isLoading) {
    return (
      <PublicShell routeKey="home">
        <div className="page-shell flex min-h-[60vh] items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      </PublicShell>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const activeMarketName = locality?.name ?? region?.name ?? 'Your market';
  const geographyLinks = region ? getGeographyLinks(region.slug, locality?.slug) : null;
  const enrollmentGroups = getEnrollmentDashboardGroups(enrollments);
  const upcomingEnrollments = enrollmentGroups.upcoming.slice(0, 3);
  const attentionEnrollments = enrollmentGroups.attention.slice(0, 2);
  const pastCount = enrollmentGroups.past.length;
  const sessionTimezone = locality?.timezone ?? region?.timezone ?? 'America/Indiana/Indianapolis';

  const quickActions = geographyLinks
    ? [
        {
          title: 'Open local calendar',
          body: 'See what is happening next in your active market without resetting geography.',
          href: geographyLinks.calendarHref,
        },
        {
          title: 'Browse nearby classes',
          body: 'Stay in the same region or locality while looking for another class to book.',
          href: geographyLinks.classesHref,
        },
        {
          title: 'Meet local creators',
          body: 'See who is teaching in your selected market and move straight into their profiles.',
          href: geographyLinks.creatorsHref,
        },
        {
          title: 'Search this market',
          body: 'Use unified search with your current region and locality already applied.',
          href: geographyLinks.searchHref,
        },
      ]
    : [];

  return (
    <PublicShell routeKey="home">
      <section className="hero-gradient border-b border-border/60 py-14">
        <div className="page-shell">
          <div className="max-w-4xl">
            <div className="flex flex-wrap items-center gap-2">
              <Badge className="rounded-full bg-primary/10 px-3 py-1 text-primary">
                Signed-in overview
              </Badge>
              <Badge variant="outline" className="rounded-full px-3 py-1">
                <MapPin className="mr-1.5 h-3.5 w-3.5" />
                {activeMarketName}
              </Badge>
            </div>
            <h1 className="mt-5 text-4xl font-semibold tracking-tight text-foreground md:text-5xl">
              {getContent('welcome_title', 'Welcome back')}, {user?.firstName || 'friend'}.
            </h1>
            <p className="mt-4 max-w-2xl text-lg leading-8 text-muted-foreground">
              {upcomingEnrollments.length > 0
                ? 'Your next class, local calendar, and market-specific discovery links are all in one place.'
                : 'Jump back into your active geography, browse classes, and keep your account history close by.'}
            </p>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <OverviewStatCard
                icon={CalendarDays}
                label="Upcoming"
                value={String(enrollmentGroups.upcoming.length)}
                detail={
                  upcomingEnrollments[0]?.sessionStartDateTime
                    ? formatDateCompact(upcomingEnrollments[0].sessionStartDateTime)
                    : 'No booked sessions yet'
                }
              />
              <OverviewStatCard
                icon={Compass}
                label="Past classes"
                value={String(pastCount)}
                detail={pastCount > 0 ? 'Your completed class history' : 'Nothing completed yet'}
              />
              <OverviewStatCard
                icon={Users}
                label="Needs attention"
                value={String(enrollmentGroups.attention.length)}
                detail={
                  enrollmentGroups.attention.length > 0
                    ? 'Refund or cancellation updates waiting'
                    : 'No refund or cancellation issues'
                }
              />
              <OverviewStatCard
                icon={MapPin}
                label="Active market"
                value={activeMarketName}
                detail={locality ? (region?.name ?? 'Locality selected') : 'Region-wide browsing'}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="page-section bg-background">
        <div className="page-shell grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
          <div className="space-y-4">
            <div>
              <p className="editorial-kicker">Quick actions</p>
              <h2 className="mt-3 text-3xl font-semibold text-foreground">Stay in your market</h2>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {quickActions.map(action => (
                <Link key={action.href} href={action.href}>
                  <Card className="h-full rounded-[1.5rem] border-border/70 transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-sm">
                    <CardContent className="flex h-full flex-col justify-between gap-4 p-5">
                      <div>
                        <h3 className="text-lg font-semibold text-foreground">{action.title}</h3>
                        <p className="mt-2 text-sm leading-6 text-muted-foreground">
                          {action.body}
                        </p>
                      </div>
                      <span className="inline-flex items-center gap-2 text-sm font-medium text-primary">
                        Open
                        <ArrowRight className="h-4 w-4" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>

          <Card className="rounded-[1.75rem] border-border/70 shadow-sm">
            <CardContent className="space-y-4 p-6">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="editorial-kicker">Upcoming classes</p>
                  <h2 className="mt-2 text-2xl font-semibold text-foreground">
                    {upcomingEnrollments.length > 0
                      ? 'Next on your calendar'
                      : 'Nothing booked yet'}
                  </h2>
                </div>
                <Link href="/account/enrollments">
                  <Button variant="outline" className="rounded-full px-5">
                    View all
                  </Button>
                </Link>
              </div>

              {upcomingEnrollments.length > 0 ? (
                <div className="space-y-3">
                  {upcomingEnrollments.map(enrollment => (
                    <div
                      key={enrollment.id}
                      className="rounded-[1.25rem] border border-border/60 bg-muted/35 p-4"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <h3 className="font-semibold text-foreground">
                            {enrollment.classTitle || 'Untitled class'}
                          </h3>
                          <p className="mt-1 text-sm text-muted-foreground">
                            {enrollment.creatorName || 'Unknown creator'}
                          </p>
                        </div>
                        <Badge variant="outline" className="rounded-full px-3 py-1">
                          {formatEnrollmentStatusLabel(enrollment.status)}
                        </Badge>
                      </div>
                      {enrollment.sessionStartDateTime ? (
                        <p className="mt-3 text-sm leading-6 text-muted-foreground">
                          {formatSessionTime(
                            enrollment.sessionStartDateTime,
                            enrollment.sessionEndDateTime ?? enrollment.sessionStartDateTime,
                            sessionTimezone
                          )}
                        </p>
                      ) : null}
                      <p className="mt-2 text-sm text-muted-foreground">
                        {enrollment.quantity ?? 1} ticket
                        {(enrollment.quantity ?? 1) === 1 ? '' : 's'} booked
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-[1.25rem] border border-dashed border-border/70 bg-muted/20 p-5 text-sm text-muted-foreground">
                  Book a class, then come back here for your next session, account history, and
                  refund or cancellation updates.
                </div>
              )}

              {attentionEnrollments.length > 0 ? (
                <div className="rounded-[1.25rem] border border-amber-300/70 bg-amber-50/80 p-4">
                  <p className="font-medium text-amber-900">Refund or cancellation updates</p>
                  <ul className="mt-2 space-y-1 text-sm text-amber-800">
                    {attentionEnrollments.map(enrollment => (
                      <li key={enrollment.id}>
                        {enrollment.classTitle || 'Class'}:{' '}
                        {formatEnrollmentStatusLabel(enrollment.status)}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="page-section bg-muted/35">
        <div className="page-shell">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="editorial-kicker">Keep exploring</p>
              <h2 className="mt-3 text-3xl font-semibold text-foreground">Recommended classes</h2>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
                Fresh classes to explore after you have checked your upcoming sessions.
              </p>
            </div>
            <Link href={geographyLinks?.classesHref ?? '/classes'}>
              <Button className="rounded-full px-5" data-testid="button-view-all-classes-top">
                View classes
              </Button>
            </Link>
          </div>

          <div className="mt-8 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {featuredClasses.slice(0, 3).map(classItem => (
              <ClassCard
                key={classItem.id}
                id={classItem.id}
                title={classItem.title}
                description={classItem.description}
                price={classItem.price}
                pricingMode={classItem.pricingMode}
                suggestedPrice={classItem.suggestedPrice}
                promoEnabled={classItem.promoEnabled}
                promoPrice={classItem.promoPrice}
                thumbnailUrl={classItem.thumbnailUrl}
                rating={classItem.rating}
                creator={classItem.creator}
                spotsLeft={classItem.spotsLeft}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="creator-spotlight page-section">
        <div className="page-shell">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="editorial-kicker">Creators to watch</p>
              <h2 className="mt-3 text-3xl font-semibold text-foreground">
                Meet your local experts
              </h2>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
                Discover the people teaching in and around your selected market.
              </p>
            </div>
            <Link href={geographyLinks?.creatorsHref ?? '/creators'}>
              <Button
                variant="outline"
                className="rounded-full px-5"
                data-testid="button-discover-creators"
              >
                View creators
              </Button>
            </Link>
          </div>

          <div className="mt-8 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {featuredCreators.map(creator => (
              <div key={creator.id} className="w-full">
                <CreatorCard
                  id={creator.id}
                  displayName={creator.displayName}
                  creatorHeadline={creator.creatorHeadline}
                  bio={creator.bio}
                  profileImageUrl={creator.profileImageUrl}
                  isVerified={creator.isVerified}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {publicStats && <CommunitySection publicStats={publicStats} />}
    </PublicShell>
  );
}

function OverviewStatCard({
  icon: Icon,
  label,
  value,
  detail,
}: {
  icon: typeof CalendarDays;
  label: string;
  value: string;
  detail: string;
}) {
  return (
    <Card className="rounded-[1.5rem] border-border/70 shadow-sm">
      <CardContent className="space-y-3 p-5">
        <Icon className="h-5 w-5 text-primary" />
        <div>
          <p className="text-2xl font-semibold text-foreground">{value}</p>
          <p className="text-sm font-medium text-foreground/80">{label}</p>
        </div>
        <p className="text-sm leading-6 text-muted-foreground">{detail}</p>
      </CardContent>
    </Card>
  );
}
